import React from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Save } from 'lucide-react';
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger, } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { TextField } from '@/components/text-field';
import { TextAreaField } from '@/components/textarea-field';
import { errorHandler } from '@/lib/utils';
import { useSaveAsDesign } from '../hooks/use-workspace-page-designs';
/**
 * Pro-only "Save as design" affordance. Snapshots the currently-applied
 * tokens into a new WorkspacePageDesign so the SME can build a brand
 * theme once and reuse it across every campaign in the workspace.
 */
export function SaveAsDesignDialog({ workspaceId, baseDesign, proUnlocked, onSaved, }) {
    const { t } = useTranslation('campaign');
    const [open, setOpen] = React.useState(false);
    const form = useForm({
        defaultValues: { name: '', description: '' },
    });
    const { mutate, isPending } = useSaveAsDesign();
    React.useEffect(() => {
        if (open) {
            form.reset({
                // Suffix lives in the i18n bundle so non-English users
                // don't end up with "Audacieux (custom)" mixed locales.
                name: baseDesign
                    ? t('save_as_design.name_default', {
                        defaultValue: '{{name}} (custom)',
                        name: baseDesign.name,
                    })
                    : '',
                description: '',
            });
        }
    }, [open, baseDesign, form, t]);
    const onSubmit = (values) => {
        if (!values.name.trim()) {
            return;
        }
        mutate({
            workspaceId,
            data: {
                name: values.name.trim(),
                description: values.description?.trim() || undefined,
                // Snapshot the current tokens so the saved design renders
                // identically to what the user is looking at. baseDesignId
                // is sent as a fallback signal (server will seed from
                // "minimal" if neither is supplied).
                tokens: baseDesign?.tokens,
                baseDesignId: baseDesign?.id,
            },
        }, {
            onSuccess: (data) => {
                toast.success(data.message);
                setOpen(false);
                onSaved?.(data.design);
            },
            onError: errorHandler,
        });
    };
    return (<Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button variant="outline" size="sm" disabled={!proUnlocked} className="gap-2">
                    <Save className="h-4 w-4"/>
                    {t('save_as_design.button', { defaultValue: 'Save as design' })}
                </Button>
            </DialogTrigger>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>{t('save_as_design.title', { defaultValue: 'Save as workspace design' })}</DialogTitle>
                    <DialogDescription>
                        {t('save_as_design.description', {
            defaultValue: 'Snapshot the current theme so every campaign in the workspace can reuse it.',
        })}
                    </DialogDescription>
                </DialogHeader>

                <Form {...form}>
                    <div className="space-y-4">
                        <TextField control={form.control} name="name" label={t('save_as_design.name_label', { defaultValue: 'Design name' })} placeholder={t('save_as_design.name_placeholder', {
            defaultValue: 'e.g. Brand 2026',
        })} required autoFocus disabled={isPending} maxLength={80}/>
                        <TextAreaField control={form.control} name="description" label={t('save_as_design.description_label', { defaultValue: 'Notes (optional)' })} placeholder={t('save_as_design.description_placeholder', {
            defaultValue: 'When to use this design…',
        })} disabled={isPending} maxLength={2000}/>
                    </div>
                </Form>

                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" disabled={isPending}>
                            {t('save_as_design.cancel', { defaultValue: 'Cancel' })}
                        </Button>
                    </DialogClose>
                    <Button onClick={form.handleSubmit(onSubmit)} disabled={isPending}>
                        {isPending
            ? t('save_as_design.saving', { defaultValue: 'Saving…' })
            : t('save_as_design.save', { defaultValue: 'Save design' })}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>);
}
